import { useHasProperty } from "@/hooks";
import { RootState } from "@/services/redux/store";
import { comparisonUtils, FILTERED_PRODUCT_PROPS } from "@/utils";
import { Box, Button } from "@mui/material";
import React, { useState } from "react";
import { GridContextProvider, GridDropZone, GridItem } from "react-grid-dnd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SideModal from "../SideModal";
import ProductCard from "./components/ProductCard";
import { NB_PRODUCT_TO_SHOW } from "./enums";
import { arrangeProducts, updatePositionsOnDrag } from "./helpers";
import { IDraggablePinProductListProps } from "./types";

const DraggablepinProductList: React.FC<IDraggablePinProductListProps> = ({
  items,
  pinnedItems,
  setPinnedItems,
  itemsPerRow,
  categoryId,
  forceReload,
  fieldsSelected,
}) => {
  const { t } = useTranslation();
  const additionnalProperties = useSelector(
    (state: RootState) => state.product.properties
  );
  const { hasBuilder } = useHasProperty();
  const currency = useSelector((state: RootState) => state.catalog.currency);
  const arrangedItems = arrangeProducts(items, pinnedItems);

  const onChange = (
    sourceId: string,
    sourceIndex: number,
    targetIndex: number,
    targetId: string | undefined
  ) => {
    setPinnedItems(
      updatePositionsOnDrag(
        arrangedItems,
        pinnedItems,
        sourceIndex,
        targetIndex
      )
    );
  };

  const pin = (productId: string, index: number) => {
    setPinnedItems({ ...pinnedItems, [index + 1]: productId });
  };

  const unpin = (index: number) => {
    setPinnedItems(
      Object.fromEntries(
        Object.entries(pinnedItems)?.filter(
          (entry: Array<any>) => entry[0] != index + 1
        )
      )
    );
    forceReload();
  };

  const [openSearchProduct, setOpenSearchProduct] = useState<number | null>(
    null
  );
  const handleOpen = (index: number) => setOpenSearchProduct(index);
  const handleClose = () => setOpenSearchProduct(null);
  const push = (product: Dic<any>) => {
    setPinnedItems(
      updatePositionsOnDrag(
        [...arrangedItems, product],
        pinnedItems,
        arrangedItems.length,
        openSearchProduct ? openSearchProduct + 1 : 0
      )
    );
    forceReload();
  };

  const rowHeight =
    250 +
    40 *
      (additionnalProperties?.filter(
        (p: string) =>
          !FILTERED_PRODUCT_PROPS?.find(comparisonUtils.areEquals(p))
      )?.length +
        1);

  const [nbProductToShow, setNbProductToShow] = useState(NB_PRODUCT_TO_SHOW);
  const handleSeeMore = () => {
    const newTotal =
      nbProductToShow + NB_PRODUCT_TO_SHOW < arrangedItems.length
        ? nbProductToShow + NB_PRODUCT_TO_SHOW
        : arrangedItems.length;
    setNbProductToShow(newTotal);
  };
  const length =
    arrangedItems.length < nbProductToShow
      ? arrangedItems.length
      : nbProductToShow;
  const nbRows = Math.ceil(length / itemsPerRow);

  return (
    <>
      <SideModal
        isOpen={openSearchProduct !== null}
        onClose={handleClose}
        child="search_product"
        position="right"
        params={{
          addProduct: push,
          param: { categoryId },
        }}
      />
      <GridContextProvider onChange={onChange}>
        <GridDropZone
          id="items"
          boxesPerRow={itemsPerRow}
          rowHeight={rowHeight}
          style={{
            width: "100%",
            height: rowHeight * nbRows,
          }}
        >
          {arrangedItems.slice(0, nbProductToShow).map((item, index) => {
            if (!!item?.id)
              return (
                <GridItem key={item.id}>
                  <div style={{ width: "100%", height: "100%" }}>
                    <ProductCard
                      product={item}
                      productPerRow={4}
                      unpin={unpin}
                      showUnpin={pinnedItems[index + 1] === item?.id}
                      pin={pin}
                      setProductIndex={() => handleOpen(index)}
                      pinnable={true}
                      index={index}
                      currency={currency}
                    />
                  </div>
                </GridItem>
              );
          })}
        </GridDropZone>
      </GridContextProvider>
      {nbProductToShow < arrangedItems.length && (
        <Box
          sx={{
            width: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button id="see-more" onClick={handleSeeMore}>
            {t("see_more")}
          </Button>
        </Box>
      )}
    </>
  );
};

export default DraggablepinProductList;
